/**
 * Sync Issues page — lists every row in visit_sync_queue with
 * status='failed' and lets the rep either retry (reset to pending,
 * clear failure metadata, kick the engine) or discard (delete the
 * queue row AND its optimistic local visit_task_sessions row).
 *
 * Discarding is destructive: data captured offline is lost. A
 * window.confirm gate makes that explicit.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { STORES, getAll, put, remove } from '../utils/indexedDB';
import { isCorrectable } from '../sync/failedOpRecovery';

interface FailedRow {
  id: number;
  action: string;
  payload: Record<string, unknown>;
  failedReason?: string;
  createdAt: string;
  relatedTaskUUID?: string;
  retries: number;
  maxRetries: number;
  endpoint: string;
  method: string;
  status: 'pending' | 'syncing' | 'failed';
  priority: number;
}

/**
 * A failed row is fixable in-app when its reason is a correctable server
 * validation AND it's a visit-create op we can reopen: a check-in (#2082) or a
 * manual / after-the-fact entry (#3429). Dependents (photo/survey) are not
 * independently fixable — they resolve once their parent is fixed.
 */
function canFix(row: FailedRow): boolean {
  return (row.action === 'checkIn' || row.action === 'createManualVisit') && isCorrectable(row.failedReason);
}

export default function SyncIssues() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FailedRow[]>([]);

  const refresh = useCallback(async () => {
    const all = (await getAll(STORES.VISIT_SYNC_QUEUE)) as FailedRow[];
    setRows(all.filter((r) => r.status === 'failed'));
  }, []);

  useEffect(() => {
    void refresh();
    const onEvent = () => void refresh();
    window.addEventListener('rdm:sync-enqueued', onEvent);
    window.addEventListener('rdm:sync-failed', onEvent);
    return () => {
      window.removeEventListener('rdm:sync-enqueued', onEvent);
      window.removeEventListener('rdm:sync-failed', onEvent);
    };
  }, [refresh]);

  const handleRetry = async (row: FailedRow) => {
    await put(STORES.VISIT_SYNC_QUEUE, {
      ...row,
      status: 'pending',
      retries: 0,
      failedReason: undefined,
    });
    window.dispatchEvent(new Event('rdm:sync-enqueued'));
    void refresh();
  };

  const handleDiscard = async (row: FailedRow) => {
    // A visit-create op (check-in / manual entry) can have queued dependents
    // (uploadPhoto / submitSurvey) that key on `dependsOn === this visit's
    // localId`. Discarding the parent orphans them permanently ("Parent visit
    // never synced"), so they must go too (#3429).
    const all = (await getAll(STORES.VISIT_SYNC_QUEUE)) as FailedRow[];
    const dependents = row.relatedTaskUUID
      ? all.filter((r) => r.id !== row.id && (r as { dependsOn?: string }).dependsOn === row.relatedTaskUUID)
      : [];
    const payloadSummary = JSON.stringify(row.payload).slice(0, 80);
    const dependentLine = dependents.length
      ? `\n\nThis will also discard ${dependents.length} dependent item(s) (photos/survey) that can't sync without it.`
      : '';
    const ok = window.confirm(
      `Discard this sync item permanently? Captured data will be deleted:\n\n${payloadSummary}…${dependentLine}\n\nThis cannot be undone.`,
    );
    if (!ok) return;
    await remove(STORES.VISIT_SYNC_QUEUE, row.id);
    for (const dep of dependents) {
      await remove(STORES.VISIT_SYNC_QUEUE, dep.id);
    }
    if (row.relatedTaskUUID) {
      const tasks = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<{ uuid?: string }>;
      const match = tasks.find((t) => t.uuid === row.relatedTaskUUID);
      if (match) {
        await remove(STORES.VISIT_TASK_SESSIONS, row.relatedTaskUUID);
      }
    }
    // Notify event-driven listeners (SyncIssuesBanner) that the queue changed so
    // the failed-sync banner re-counts and hides when nothing is left. Discard is
    // a queue mutation like retry/enqueue; without this the banner stays stale
    // (#2080). Mirrors handleRetry's dispatch.
    window.dispatchEvent(new Event('rdm:sync-enqueued'));
    void refresh();
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 pb-24">
      <h1 className="font-heading text-xl font-semibold text-slate-800">Sync Issues</h1>
      <p className="mt-1 text-sm text-slate-500">
        These items couldn't be synced to the server. Tap Retry to try again once the
        underlying problem is fixed, or Discard to permanently delete the captured
        data.
      </p>

      {rows.length === 0 && (
        <div className="mt-8 rounded-xl bg-white p-6 text-center text-slate-500">
          No sync issues. Everything is up to date.
        </div>
      )}

      <ul className="mt-4 space-y-3">
        {rows.map((row) => (
          <li key={row.id} className="rounded-xl bg-white p-4 shadow-card">
            <div className="flex items-center justify-between">
              <span className="font-medium text-slate-800">{row.action}</span>
              <span className="text-xs text-slate-400">
                {new Date(row.createdAt).toLocaleString()}
              </span>
            </div>
            {row.failedReason && (
              <p className="mt-2 rounded bg-red-50 p-2 text-sm text-red-700">
                {row.failedReason}
              </p>
            )}
            <details className="mt-2">
              <summary className="cursor-pointer text-xs text-slate-500">Show payload</summary>
              <pre className="mt-1 overflow-x-auto rounded bg-slate-100 p-2 text-xs">
                {JSON.stringify(row.payload, null, 2)}
              </pre>
            </details>
            <div className="mt-3 flex gap-2">
              {canFix(row) && (
                <button
                  type="button"
                  onClick={() => navigate(`/edit-visit/draft/${row.id}`)}
                  className="flex-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                >
                  Fix
                </button>
              )}
              <button
                type="button"
                onClick={() => handleRetry(row)}
                className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium ${
                  canFix(row)
                    ? 'border border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                Retry
              </button>
              <button
                type="button"
                onClick={() => handleDiscard(row)}
                className="flex-1 rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
              >
                Discard
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
